'use client';

import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, Chip } from '@components/common';
import {
  PaperAirplaneIcon,
} from '@heroicons/react/20/solid';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import BestieAvatar from '../bestie/BestieAvatar';

export const BestieBanner = () => {
  const { t } = useTranslation('common');
  const [input, setInput] = useState('');
  const { router } = useExtendedRouter();

  const handleSearch = (query: string) => {
    if (!query) return;
    void router.push(`/messages?type=talent&bestie=true&q=${encodeURIComponent(query)}`);
  };

  const actionButtons = [
    { label: t('homepage.chip.find_job') },
    { label: t('homepage.chip.start_interview') },
    { label: t('call.me') },
  ];

  const inputAndChips = (
    <div className="mt-5 flex w-full flex-col items-start">
      {/* Only this inner div expands on focus */}
      <div className="w-full max-w-2xl transition-all duration-700 ease-in-out focus-within:max-w-5xl">
        <div className="group relative flex items-center rounded-full border-2 border-ink/15 bg-surface p-0.5 transition-all duration-700 focus-within:border-secondary/30">
          <ChatTextarea
            input={input}
            handleInputChange={(e) => setInput(e.target.value)}
            handleKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch(input);
              }
            }}
            placeholder={t('homepage.chat.input.placeholder')}
            customWrapperStyle="flex-1 bg-surface overflow-hidden rounded-full"
            customTextareaStyle="bg-surface scrollbar-hide text-ink overflow-y-hidden placeholder:text-ink placeholder:text-base px-4 text-lg focus:ring-0 focus:ring-offset-0 outline-none border-none shadow-none whitespace-nowrap overflow-x-auto resize-none relative cursor-text pointer-events-auto placeholder:truncate placeholder:overflow-ellipsis leading-7 placeholder:leading-7"
            rows={1}
            textareaSizeStyle="w-full h-7"
            textareaWrapperSizeStyle="w-full h-7 relative"
          />
          <Button
            color="secondary"
            rounding="full"
            className="size-10 shrink-0 !p-3"
            onClick={() => handleSearch(input)}
          >
            <PaperAirplaneIcon className="size-5 text-white" />
          </Button>
        </div>
      </div>

      {/* Chips outside focus-within but inside the same max-w-2xl parent */}
      <div className="mt-3 flex w-full max-w-2xl flex-wrap justify-center gap-2">
        {actionButtons.map((btn) => (
          <Chip
            key={btn.label}
            onClick={() => handleSearch(btn.label)}
            className="bg-surface px-3 py-2 text-ink shadow-sm transition-all hover:scale-105 hover:border-0 hover:!border-transparent hover:bg-secondary/15 hover:!text-secondary active:scale-95"
          >
            {btn.label}
          </Chip>
        ))}
      </div>
    </div>
  );

  return (
    <div className="z-0 mb-5 flex w-full flex-col items-center pb-0 pt-2 text-center">
      {/* Desktop: Avatar + Title/Input/Chips side by side */}
      <div className="hidden w-full md:flex md:flex-row md:items-start md:justify-center md:gap-6 md:text-left">
        <div className="relative shrink-0 self-start">
          <BestieAvatar className="size-35" />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-tight">
            {t('homepage.hero.title')}
          </h1>
          {inputAndChips}
        </div>
      </div>

      {/* Mobile: Avatar + Title row, then Input/Chips below */}
      <div className="w-full md:hidden">
        <div className="mb-3 flex items-center gap-3 text-left">
          <div className="relative shrink-0">
            <BestieAvatar className="size-12" />
          </div>
          <h1 className="text-xl font-bold leading-tight">
            {t('homepage.hero.title')}
          </h1>
        </div>
        {inputAndChips}
      </div>
    </div>
  );
};
